import { useState } from "react";
import { Settings, Palette, RotateCcw, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTheme } from "@/components/ThemeProvider";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AccountSection } from "./AccountSection";
import { DangerZoneSection } from "./DangerZoneSection";
import { AboutSection } from "./AboutSection";

const ONBOARDING_KEY = "settleup_onboarding_completed";

export function SettingsSheet() {
  const { theme, setTheme } = useTheme();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [open, setOpen] = useState(false);

  const handleResetOnboarding = () => {
    localStorage.removeItem(ONBOARDING_KEY);
    toast.success("Onboarding reset! Refresh to see welcome screen.");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Settings">
          <Settings className="w-5 h-5" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[320px] sm:w-[380px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Settings
          </SheetTitle>
          <SheetDescription>
            Manage your app preferences
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Sign In for Guest Users */}
          {!isAuthenticated && (
            <>
              <Button
                onClick={() => {
                  setOpen(false);
                  navigate("/auth");
                }}
                className="w-full"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign In / Sign Up
              </Button>
              <Separator />
            </>
          )}

          {/* Account Section */}
          <AccountSection />

          <Separator />

          {/* Appearance Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Palette className="w-4 h-4" />
              Appearance
            </div>
            
            <div className="pl-6 space-y-2">
              <Label htmlFor="theme">Theme</Label>
              <Select value={theme} onValueChange={(value: "light" | "dark" | "system") => setTheme(value)}>
                <SelectTrigger id="theme" className="w-full">
                  <SelectValue placeholder="Select theme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Reset Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <RotateCcw className="w-4 h-4" />
              Reset
            </div>
            
            <div className="pl-6">
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetOnboarding}
                className="w-full justify-start"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset Welcome Screen
              </Button>
              <p className="text-xs text-muted-foreground mt-1">
                Show the onboarding tutorial again
              </p>
            </div>
          </div>

          <Separator />

          {/* About Section */}
          <AboutSection />

          {/* Danger Zone (Sign Out & Delete) */}
          {isAuthenticated && (
            <>
              <Separator />
              <DangerZoneSection onClose={() => setOpen(false)} />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
